// A stage tab has two inner tabs: Overview, the stage's sections stacked
// (its own content, the history), and Puzzles, the stage's puzzles alone.
// A contribution says which it belongs to through its registration
// defaults, `tab: 'overview' | 'puzzles'`; overview when it says nothing.
// The strip is drawn from the tabs that have contributions, in this order.
// A contribution can instead pin itself under the stage header, above the
// strip and outside both tabs, with `tab: 'header'`: always visible, such as
// the opening's one-line book section.

export type InnerTab = 'overview' | 'puzzles';

export const INNER_TABS: ReadonlyArray<{ id: InnerTab; title: string }> = [
  { id: 'overview', title: 'Overview' },
  { id: 'puzzles', title: 'Puzzles' },
];

/** Where a contribution goes: an inner tab, or pinned under the stage header. */
export type Placement = InnerTab | 'header';

/** Where a contribution's defaults put it. */
export function tabOf(defaults: { tab?: unknown } | undefined): Placement {
  if (defaults?.tab === 'header') return 'header';
  return defaults?.tab === 'puzzles' ? 'puzzles' : 'overview';
}

/** The contributions grouped by placement, each group keeping its order. */
export function groupByTab<T>(items: T[], defaultsOf: (item: T) => { tab?: unknown } | undefined): Record<Placement, T[]> {
  const groups: Record<Placement, T[]> = { header: [], overview: [], puzzles: [] };
  for (const item of items) groups[tabOf(defaultsOf(item))].push(item);
  return groups;
}

/** The tabs that have something to show, in strip order. */
export function tabsPresent(groups: Record<InnerTab, unknown[]>): InnerTab[] {
  return INNER_TABS.map((t) => t.id).filter((id) => groups[id].length > 0);
}

/** The tab to show: the one asked for if it is present, else the first present, else none. */
export function pickActive(wanted: InnerTab | undefined, present: InnerTab[]): InnerTab | undefined {
  if (wanted && present.includes(wanted)) return wanted;
  return present[0];
}
